// JOBE-0044.10: FilterComposer — top-level state machine for the
// predicate picker + value sub-popovers. Renders all popovers into a
// portal at document.body anchored via usePopoverAnchor.
//
// Lifecycle:
//   - `state.mode === 'closed'` — nothing rendered.
//   - `state.mode === 'picker'` — predicate picker open, anchored to
//     the trigger.
//   - `state.mode === 'value'` — value sub-popover open, anchored to
//     either the trigger (add path) or the chip (edit path).
//
// The host (LibraryListPage via ChipRow) feeds `roster` and
// `activeTypeValues` (for the schema-fields gating); the composer
// emits chips back via `onAppendChip` (add) or `onEditChip(index,
// values)` (edit).

import { useImperativeHandle, forwardRef, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { PredicatePicker } from './PredicatePicker.jsx';
import { usePopoverAnchor } from './usePopoverAnchor.js';
import { PeopleValuePopover } from './valuePopovers/PeopleValuePopover.jsx';
import { TypeValuePopover } from './valuePopovers/TypeValuePopover.jsx';
import { CollectionValuePopover } from './valuePopovers/CollectionValuePopover.jsx';
import { DateValuePopover } from './valuePopovers/DateValuePopover.jsx';
import { ScalarValuePopover } from './valuePopovers/ScalarValuePopover.jsx';
import { ProcedureValuePopover } from './valuePopovers/ProcedureValuePopover.jsx';
import { SchemaFieldPicker } from './valuePopovers/SchemaFieldPicker.jsx';
import { findVocabularyByFriendlyToken } from '../../lib/omni-parser/vocabulary.js';

const CLOSED = { mode: 'closed' };

// pickValuePopoverKey: maps a vocabulary entry to its sub-popover key.
// Pure; tested via the picker's vocabulary table.
export function pickValuePopoverKey(entry) {
  if (!entry) return 'scalar';
  return entry.valuePopover || 'scalar';
}

// Map a chip's friendlyToken to its vocabulary entry + sub-popover.
// `field.X` is the special case (synthesised, not in the vocab table).
function entryForChipToken(token) {
  if (!token) return null;
  if (token.startsWith('field.')) {
    return {
      friendlyToken: token,
      wirePredicate: token.slice('field.'.length),
      valuePopover: 'scalar',
      label: token,
      gloss: 'schema field',
      group: 'Schema',
    };
  }
  return findVocabularyByFriendlyToken(token);
}

function ScrimAndPopover({ anchorRef, children, onScrimClick }) {
  const style = usePopoverAnchor(anchorRef);
  return (
    <>
      <div
        className="library-composer-scrim"
        onClick={onScrimClick}
        aria-hidden="true"
      />
      <div className="library-composer-anchor" style={style}>
        {children}
      </div>
    </>
  );
}

export const FilterComposer = forwardRef(function FilterComposer({
  triggerRef,
  roster,
  activeTypeValues,
  onAppendChip,
  onEditChip,
}, ref) {
  const [state, setState] = useState(CLOSED);
  // Anchor ref for edit-mode (chip-anchored) sub-popovers. We hold the
  // host's own ref object directly so that if the chip unmounts/re-keys
  // the wrapped `.current` follows React's update (rather than pointing
  // at a detached node, as a synthesised `{ current: node }` wrapper
  // would). See PR #693 review LOW-2.
  const editAnchorRefRef = useRef(null);

  const openPicker = useCallback(() => {
    setState({ mode: 'picker' });
  }, []);

  const close = useCallback(() => {
    setState(CLOSED);
    editAnchorRefRef.current = null;
  }, []);

  // Open a value sub-popover for an existing chip (edit path).
  const openForEdit = useCallback((opts) => {
    const { chip, chipIndex, anchorRef } = opts || {};
    if (!chip) return;
    const entry = entryForChipToken(chip.friendlyToken);
    if (!entry) return;
    // Capture the caller's ref object directly — no synthetic wrapper.
    editAnchorRefRef.current = anchorRef && anchorRef.current ? anchorRef : null;
    setState({
      mode: 'value',
      path: 'edit',
      entry,
      popoverKey: pickValuePopoverKey(entry),
      chipIndex,
      initialValues: chip.values || [],
      // For field.X chips, surface the field name through to the
      // SchemaFieldPicker so step 1 is skipped.
      fieldName: chip.friendlyToken && chip.friendlyToken.startsWith('field.')
        ? chip.friendlyToken.slice('field.'.length)
        : '',
    });
  }, []);

  useImperativeHandle(ref, () => ({
    openPicker,
    openForEdit,
    close,
  }), [openPicker, openForEdit, close]);

  // Picker → predicate chosen → open the corresponding sub-popover.
  const onPickPredicate = useCallback((entry) => {
    setState({
      mode: 'value',
      path: 'add',
      entry,
      popoverKey: pickValuePopoverKey(entry),
      chipIndex: -1,
      initialValues: [],
      fieldName: '',
    });
  }, []);

  const onPickSchemaField = useCallback(() => {
    setState({
      mode: 'value',
      path: 'add',
      entry: { friendlyToken: 'field', valuePopover: 'schema', group: 'Schema' },
      popoverKey: 'schema',
      chipIndex: -1,
      initialValues: [],
      fieldName: '',
    });
  }, []);

  // Sub-popover Esc / cancel:
  //  - add path → close sub-popover, re-open picker (per spec).
  //  - edit path → close everything (per spec).
  const onSubCancel = useCallback(() => {
    setState((s) => {
      if (s.mode !== 'value') return CLOSED;
      if (s.path === 'add') return { mode: 'picker' };
      return CLOSED;
    });
  }, []);

  // Sub-popover submit:
  //  - add path → onAppendChip; close.
  //  - edit path → onEditChip(chipIndex, values); close.
  const onSubSubmit = useCallback((values) => {
    setState((s) => {
      if (s.mode !== 'value') return s;
      const entry = s.entry;
      // SchemaFieldPicker submits { fieldName, values } — synthesise
      // the field.X chip.
      if (s.popoverKey === 'schema' && values && typeof values === 'object'
          && !Array.isArray(values)) {
        const { fieldName, values: vs } = values;
        const friendlyToken = `field.${fieldName}`;
        const chip = {
          kind: 'predicate',
          friendlyToken,
          wirePredicate: fieldName,
          values: vs,
        };
        if (s.path === 'add' && onAppendChip) onAppendChip(chip);
        else if (s.path === 'edit' && onEditChip) onEditChip(s.chipIndex, chip);
        return CLOSED;
      }
      // ProcedureValuePopover submits arrays directly; treat uniformly.
      const arr = Array.isArray(values) ? values : [];
      const chip = {
        kind: 'predicate',
        friendlyToken: entry.friendlyToken,
        wirePredicate: entry.wirePredicate || entry.friendlyToken,
        values: arr,
      };
      if (s.path === 'add' && onAppendChip) onAppendChip(chip);
      else if (s.path === 'edit' && onEditChip) onEditChip(s.chipIndex, chip);
      return CLOSED;
    });
  }, [onAppendChip, onEditChip]);

  if (state.mode === 'closed') return null;

  // Pick the anchor: picker always anchors to the trigger; sub-popover
  // anchors to the trigger on add and to the captured edit-anchor on
  // edit.
  let anchorRef = triggerRef;
  if (state.mode === 'value' && state.path === 'edit' && editAnchorRefRef.current) {
    anchorRef = editAnchorRefRef.current;
  }

  const body = state.mode === 'picker' ? (
    <PredicatePicker
      activeTypeValues={activeTypeValues}
      onPickPredicate={onPickPredicate}
      onPickSchemaField={onPickSchemaField}
      onClose={close}
    />
  ) : (
    renderValuePopover({
      state,
      roster,
      activeTypeValues,
      onSubmit: onSubSubmit,
      onCancel: onSubCancel,
    })
  );

  return createPortal(
    <ScrimAndPopover anchorRef={anchorRef} onScrimClick={close}>
      {body}
    </ScrimAndPopover>,
    document.body,
  );
});

// placeholderForScalar: per-entry placeholder for the ScalarValuePopover.
// Schema fields use a value-oriented hint (the field name is already
// visible in the chip header); URN-typed predicates use the
// canonical "urn:jobe:…" placeholder.
function placeholderForScalar(entry) {
  if (!entry) return 'urn:jobe:…';
  if (entry.friendlyToken && entry.friendlyToken.startsWith('field.')) {
    return 'value';
  }
  return 'urn:jobe:…';
}

function renderValuePopover({
  state, roster, activeTypeValues, onSubmit, onCancel,
}) {
  const initialValues = state.initialValues || [];
  switch (state.popoverKey) {
    case 'people':
      return (
        <PeopleValuePopover
          roster={roster}
          initialValues={initialValues}
          onSubmit={onSubmit}
          onCancel={onCancel}
        />
      );
    case 'type':
      return (
        <TypeValuePopover
          initialValues={initialValues}
          onSubmit={onSubmit}
          onCancel={onCancel}
        />
      );
    case 'collection':
      return (
        <CollectionValuePopover
          initialValues={initialValues}
          onSubmit={onSubmit}
          onCancel={onCancel}
        />
      );
    case 'date':
      return (
        <DateValuePopover
          initialValues={initialValues}
          onSubmit={onSubmit}
          onCancel={onCancel}
        />
      );
    case 'procedure':
      return (
        <ProcedureValuePopover
          initialValues={initialValues}
          onSubmit={onSubmit}
          onCancel={onCancel}
        />
      );
    case 'schema': {
      const activeType = Array.isArray(activeTypeValues)
        && activeTypeValues.length === 1
        ? activeTypeValues[0] : null;
      return (
        <SchemaFieldPicker
          activeType={activeType}
          initialValues={initialValues}
          initialFieldName={state.fieldName || ''}
          onSubmit={onSubmit}
          onCancel={onCancel}
        />
      );
    }
    case 'scalar':
    default:
      return (
        <ScalarValuePopover
          initialValues={initialValues}
          placeholder={placeholderForScalar(state.entry)}
          onSubmit={onSubmit}
          onCancel={onCancel}
        />
      );
  }
}
